import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, X, Eye, EyeOff } from "lucide-react";
import ImageUpload from "../../components/ImageUpload";
import ModalPortal from "../../components/ModalPortal";

const EMPTY = {
  title: "",
  excerpt: "",
  category: "AI",
  read: "5 min",
  author: "Optivize Team",
  cover: "",
  body_text: "",
  published: true,
};

function parseBody(text) {
  // Lines starting with "## " become {h2}, blank lines split paragraphs.
  const blocks = [];
  const lines = text.split("\n");
  let buf = [];
  const flushBuf = () => {
    const t = buf.join(" ").trim();
    if (t) blocks.push({ text: t });
    buf = [];
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (line.startsWith("## ")) {
      flushBuf();
      blocks.push({ h2: line.replace(/^##\s+/, "") });
    } else if (!line) {
      flushBuf();
    } else {
      buf.push(line);
    }
  }
  flushBuf();
  return blocks;
}

function serializeBody(blocks) {
  if (!blocks) return "";
  return blocks.map(b => b.h2 ? `## ${b.h2}` : b.text || "").join("\n\n");
}

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | post.id
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/blog/admin/all");
      setPosts(data);
    } catch { toast.error("Failed to load posts"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (p) => {
    setForm({
      title: p.title,
      excerpt: p.excerpt,
      category: p.category,
      read: p.read,
      author: p.author,
      cover: p.cover,
      body_text: serializeBody(p.body),
      published: !!p.published,
    });
    setEditing(p.id);
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const body = parseBody(form.body_text);
      const payload = {
        title: form.title,
        excerpt: form.excerpt,
        category: form.category,
        read: form.read,
        author: form.author,
        cover: form.cover,
        body,
        published: form.published,
      };
      if (editing === "new") {
        await api.post("/blog", payload);
        toast.success("Post created");
      } else {
        await api.put(`/blog/${editing}`, payload);
        toast.success("Post updated");
      }
      close();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/blog/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const togglePub = async (p) => {
    try { await api.put(`/blog/${p.id}`, { published: !p.published }); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white" style={{fontFamily:'Outfit'}}>Blog CMS</h2>
          <p className="text-slate-400 text-sm">{posts.length} posts · Mongo-backed · Markdown-style headings (`## ` for H2)</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="blog-new-btn"><Plus size={16}/> New Post</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-blue-400"/></div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-blog-table">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-700">
              <tr>
                <th className="py-3 px-4 text-left">Title</th>
                <th className="text-left">Category</th>
                <th className="text-left">Date</th>
                <th className="text-left">Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map(p => (
                <tr key={p.id} className="border-b border-slate-800">
                  <td className="py-3 px-4 text-white">
                    <div className="font-medium">{p.title}</div>
                    <div className="text-xs text-slate-500">/{p.slug}</div>
                  </td>
                  <td className="text-slate-300">{p.category}</td>
                  <td className="text-slate-400">{p.date}</td>
                  <td>
                    {p.published
                      ? <span className="tag !text-emerald-300 !border-emerald-400/30 !bg-emerald-400/10">Published</span>
                      : <span className="tag !text-amber-300 !border-amber-400/30 !bg-amber-400/10">Draft</span>}
                  </td>
                  <td className="text-right space-x-2 pr-4">
                    <button onClick={()=>togglePub(p)} className="text-slate-400 hover:text-blue-400" title={p.published ? "Unpublish" : "Publish"}>
                      {p.published ? <EyeOff size={15}/> : <Eye size={15}/>}
                    </button>
                    <button onClick={()=>openEdit(p)} className="text-slate-400 hover:text-blue-400" data-testid={`blog-edit-${p.id}`}><Pencil size={15}/></button>
                    <button onClick={()=>del(p.id)} className="text-slate-400 hover:text-rose-400" data-testid={`blog-delete-${p.id}`}><Trash2 size={15}/></button>
                  </td>
                </tr>
              ))}
              {posts.length === 0 && (
                <tr><td colSpan="5" className="text-center py-8 text-slate-500">No posts yet. Click "New Post" to create one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ModalPortal><div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-start justify-center px-5 py-10 overflow-auto" onClick={close}>
          <form onSubmit={save} onClick={e=>e.stopPropagation()} className="card p-6 w-full max-w-3xl space-y-4" data-testid="blog-form">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-white" style={{fontFamily:'Outfit'}}>
                {editing === "new" ? "New Blog Post" : "Edit Post"}
              </h3>
              <button type="button" onClick={close} className="text-slate-400 hover:text-white"><X size={20}/></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="label">Title</label><input required value={form.title} onChange={e=>setForm({...form,title:e.target.value})} className="input" data-testid="blog-title-input"/></div>
              <div><label className="label">Category</label><input value={form.category} onChange={e=>setForm({...form,category:e.target.value})} className="input"/></div>
            </div>
            <div><label className="label">Excerpt</label><textarea required value={form.excerpt} onChange={e=>setForm({...form,excerpt:e.target.value})} className="input" rows={2}/></div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><label className="label">Author</label><input value={form.author} onChange={e=>setForm({...form,author:e.target.value})} className="input"/></div>
              <div><label className="label">Read time</label><input value={form.read} onChange={e=>setForm({...form,read:e.target.value})} className="input" placeholder="6 min"/></div>
              <div className="flex items-end pb-1.5">
                <label className="inline-flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                  <input type="checkbox" checked={form.published} onChange={e=>setForm({...form,published:e.target.checked})} className="w-4 h-4 accent-blue-500"/>
                  Published
                </label>
              </div>
            </div>
            <div><label className="label">Cover Image</label>
              <ImageUpload value={form.cover} onChange={(url)=>setForm({...form,cover:url})} aspect="16/9" label=""/>
            </div>
            <div>
              <label className="label">Body — use blank lines between paragraphs, "## " for headings</label>
              <textarea required value={form.body_text} onChange={e=>setForm({...form,body_text:e.target.value})} className="input font-mono text-[13px]" rows={12} data-testid="blog-body-input" placeholder={`The web development industry has entered a new era.\n\n## 1. Generative design systems\n\nTools like our own AI Website Builder produce page-level blueprints...`}/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={close} className="btn-ghost">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary" data-testid="blog-save-btn">
                {saving ? <Loader2 size={14} className="animate-spin"/> : null} {editing === "new" ? "Create Post" : "Save Changes"}
              </button>
            </div>
          </form>
        </div></ModalPortal>
      )}
    </div>
  );
}
